import moment from 'moment';
import { DATE_SHORTCUTS } from '@src/utils/constants';

/**
 * Shortcut → [amount, unit] offset for the comparison (previous) period.
 * Mirrors the logic in SummaryContainer/utils/dates.ts so that shortcuts like
 * "Last 6 hours" compare against the same window yesterday (−24 h) rather than
 * the immediately preceding 6-hour window.
 */
const SHORTCUT_COMPARISON_OFFSET: Record<string, [number, moment.DurationInputArg2]> = {
  [DATE_SHORTCUTS.last2Hours]: [24, 'hours'],
  [DATE_SHORTCUTS.last6Hours]: [24, 'hours'],
  [DATE_SHORTCUTS.past24Hours]: [24, 'hours'],
  [DATE_SHORTCUTS.today]: [1, 'day'],
  [DATE_SHORTCUTS.yesterday]: [1, 'day'],
  [DATE_SHORTCUTS.lastWeek]: [1, 'week'],
  [DATE_SHORTCUTS.thisMonth]: [1, 'month'],
  [DATE_SHORTCUTS.past7Days]: [7, 'days'],
  [DATE_SHORTCUTS.past30Days]: [30, 'days'],
  [DATE_SHORTCUTS.past90Days]: [90, 'days'],
};

/**
 * Calculate the DoD comparison interval for a given current interval.
 *
 * For known shortcuts the offset is taken from SHORTCUT_COMPARISON_OFFSET so
 * that e.g. "Last 6 hours" compares to the same 6-hour window 24 hours ago.
 * For all other shortcuts (including "Last 2 hours") the comparison window is
 * the immediately preceding period of the same duration.
 *
 * Returns null when the interval is incomplete.
 */
export const calcDodInterval = (interval: {
  from: string | null;
  to: string | null;
  shortcut?: string;
  granularity?: string;
}): { from: string; to: string; granularity?: string } | null => {
  if (!interval?.from || !interval?.to) return null;

  const { shortcut, from, to, granularity } = interval;

  if (shortcut && SHORTCUT_COMPARISON_OFFSET[shortcut]) {
    const [diff, unit] = SHORTCUT_COMPARISON_OFFSET[shortcut];
    return {
      from: moment(from).subtract(diff, unit).toISOString(),
      to: moment(to).subtract(diff, unit).toISOString(),
      granularity,
    };
  }

  // Fallback: shift back by the exact duration of the current window
  const duration = moment(to).diff(moment(from));
  return {
    from: moment(from).subtract(duration, 'milliseconds').toISOString(),
    to: moment(from).toISOString(),
    granularity,
  };
};

/**
 * Check if a mouse event should use default browser navigation behavior
 * (e.g., Ctrl+Click to open in new tab, Cmd+Click on Mac, Shift+Click, middle-click)
 *
 * @param e - Mouse event
 * @returns true if browser should handle the navigation, false otherwise
 */
export const shouldUseBrowserDefaultNavigation = (e: React.MouseEvent): boolean => {
  return e.ctrlKey || e.metaKey || e.shiftKey || e.button === 1;
};

/**
 * Check if a mouse event is a modifier click that should open in new tab
 * (Cmd+Click on Mac, Ctrl+Click on Windows/Linux)
 *
 * @param e - Mouse event
 * @returns true if should open in new tab
 */
export const isNewTabClick = (e: React.MouseEvent): boolean => {
  return e.metaKey || e.ctrlKey;
};
